import { Link, useNavigate } from "react-router-dom";
import { Button, buttonVariants } from "../../components/ui/button";
import { IVisitInfo } from "../../types";
import { format } from "date-fns";
import { CircleUserRound, FileText, Calendar, Video, Clock, Hospital } from "lucide-react";
import { useAppointmentData } from "../../contexts/appointment-data-context";

function PatientVisitsCard({
  doctorInfo,
  selectedPatient,
}: IVisitInfo & {
  selectedPatient: string | null;
}) {
  const navigate = useNavigate();
  const { data, setData } = useAppointmentData();

  const handleAppointmentClick = (e: React.MouseEvent, type: "WALK_IN" | "TELECONSULTATION") => {
    e.preventDefault();
    e.stopPropagation();

    setData({
      patient: data?.patient,
      appointmentType: type,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });

    navigate(`/doctors/${doctorInfo.professionalId}`);
  };

  return (
    <li className="max-sm:py-6 max-sm:first:pt-0 sm:rounded-lg sm:border sm:p-2 lg:p-4 hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-4">
        {/* Doctor Info Section */}
        <div className="flex gap-3">
          {doctorInfo.profilePhoto ? (
            <img
              src={`${doctorInfo.profilePhoto}`}
              alt={doctorInfo.fullName}
              className="size-14 shrink-0 rounded-full object-cover sm:size-18"
            />
          ) : (
            <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-muted sm:size-18">
              <CircleUserRound className="size-full" strokeWidth={1} />
            </div>
          )}

          <div className="flex-1">
            <h3 className="text-sm font-bold sm:text-lg">
              Dr {doctorInfo.fullName}
            </h3>
            {doctorInfo.specialities && (
              <p className="mb-0.5 text-xs text-muted-foreground sm:text-sm">
                {doctorInfo.specialities.join(", ")}
              </p>
            )}
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-muted-foreground" />
              <p className="text-xs font-medium text-muted-foreground sm:text-sm">
                Last visit: {format(new Date(doctorInfo.mostRecentVisitDate), "dd MMM yyyy")}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons Section - Below the photo for all devices */}
        <div className="space-y-3">
          {/* Prescription Button - Highlighted */}
          <Link
            to={`/visit-prescriptions/${doctorInfo.professionalId}`}
            className={buttonVariants({
              className:
                "w-full h-10 px-4 text-sm font-medium bg-primary/5 border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 transition-colors",
              variant: "outline",
            })}
          >
            <FileText className="w-4 h-4 mr-2" />
            View Prescription Reports
          </Link>

          {/* Appointment Booking Options - Unified for all devices */}
          <p className="text-sm font-medium text-center text-primary">
            Book Appointment
          </p>
          <div className={`grid gap-3 ${doctorInfo?.isTeleconsultationEnable ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {doctorInfo?.isTeleconsultationEnable && (
              <Button
                className="h-10 px-4 text-sm font-medium"
                variant="outline"
                onClick={(e) => handleAppointmentClick(e, "TELECONSULTATION")}
              >
                <Video className="w-4 h-4 mr-2" />
                Teleconsultation
              </Button>
            )}

            <Button
              className="h-10 px-4 text-sm font-medium"
              variant="default"
              onClick={(e) => handleAppointmentClick(e, "WALK_IN")}
            >
              <Hospital className="w-4 h-4 mr-2" />
              In-clinic
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

export default PatientVisitsCard;
